import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Reveal from "@/components/Reveal";
import CinematicHero from "@/components/CinematicHero";
import Ornament from "@/components/Ornament";
import ParallaxImage from "@/components/ParallaxImage";
import ParallaxCard from "@/components/ParallaxCard";
import MagneticButton from "@/components/MagneticButton";
import Grain from "@/components/Grain";
import SEO, {
  organizationSchema,
  websiteSchema,
  faqSchema,
} from "@/components/SEO";

export default function Home() {
  const [word, setWord] = useState(0);
  const [expertiseHover, setExpertiseHover] = useState(null);
  const words = ["Exceptional.", "Intentional.", "Rare."];
  useEffect(() => {
    const t = setInterval(() => setWord((w) => (w + 1) % words.length), 2000);
    return () => clearInterval(t);
  }, []);

  return (
    <div data-testid="page-home">
      <SEO
        title="Luxury Consulting, Training & Advisory"
        description="Next in Luxury is an international luxury advisory and execution first firm, delivering strategic consulting, training and expert implementation across the luxury industry."
        path="/"
        schema={[
          organizationSchema,
          websiteSchema,
          faqSchema([
            {
              q: "What is Next in Luxury?",
              a: "Next in Luxury is an international luxury advisory house that partners with brands, institutions and leaders to design, shape and elevate exceptional experiences through strategic consulting, executive training and expert advisory.",
            },
            {
              q: "Which sectors does Next in Luxury serve?",
              a: "Next in Luxury advises across luxury hospitality, luxury retail, tourism, culture, private clienteling, gastronomy, art de vivre and exceptional experiences.",
            },
            {
              q: "What services does Next in Luxury offer?",
              a: "Next in Luxury offers two complementary practices: Consulting — strategic advisory, brand strategy, customer experience and transformation; and Training — executive programmes, service excellence, savoir-faire and leadership transmission.",
            },
            {
              q: "Where does Next in Luxury operate?",
              a: "Next in Luxury operates worldwide with an international network of expert minds based across Europe, the Middle East, the Americas and Asia.",
            },
            {
              q: "How can I contact Next in Luxury?",
              a: "You can contact Next in Luxury through the Private Inquiry page or by email at hello@nextinluxury.com for consulting, training or advisory engagements.",
            },
          ]),
        ]}
      />
      {/* ─────────── THRESHOLD — Cinematic full-width hero ─────────── */}
      <CinematicHero>
        <h1
          className="title-caps hero-title"
          style={{
            fontSize: "clamp(2.0625rem, 6.75vw, 6.75rem)",
            color: "#F4EFE7",
            textShadow: "0 2px 40px rgba(0,0,0,0.65)",
          }}
        >
          <span
            style={{
              display: "inline-block",
              verticalAlign: "top",
              lineHeight: 1,
            }}
          >
            <span
              style={{
                display: "flex",
                justifyContent: "space-between",
                width: "100%",
              }}
            >
              <span className="hero-word w1"><span>THE</span></span>
              <span className="hero-word w2"><span>ART</span></span>
              <span className="hero-word w3"><span>OF</span></span>
            </span>
            <span
              className="hero-word w4"
              style={{ display: "block", whiteSpace: "nowrap" }}
            >
              <span>BECOMING</span>
            </span>
          </span>
          <br />
          <span
            className="font-script inline-block hero-rotating"
            style={{
              color: "#F4EFE7",
              textTransform: "none",
              letterSpacing: 0,
              fontSize: "1.4em",
              paddingLeft: "clamp(6rem, 22vw, 24rem)",
              marginTop: "-0.25em",
            }}
            key={word}
            data-testid="home-hero-rotating-word"
          >
            {words[word]}
          </span>
        </h1>
      </CinematicHero>

      {/* ═══ CHAPTER I — Founder Note (Ivory) ═══ */}
      <div className="relative" style={{ background: "#F4EFE7" }}>
        <Grain />

        {/* ─────────── OPENING NOTE — Editor's letter ─────────── */}
        <section className="relative max-w-[1440px] mx-auto px-6 md:px-12 lg:px-16 py-32 md:py-48 grid grid-cols-12 gap-8">
        <div className="col-span-12 md:col-span-4">
          <Reveal>
            <div className="hairline mb-6" />
            <p className="font-eyebrow" style={{ color: "#8C6B3F" }}>
              Founders' Note
            </p>
          </Reveal>
        </div>
        <div className="col-span-12 md:col-span-8">
          <Reveal delay={200}>
            <h2
              className="title-caps leading-[0.95]"
              style={{ fontSize: "clamp(2.25rem, 5vw, 5rem)" }}
            >
              THE TOP IS
              <br />A <span className="font-script" style={{ textTransform: "none", letterSpacing: 0, fontSize: "1.25em", color: "#8C6B3F" }}>Choice.</span>
            </h2>
            <p className="font-body mt-12 max-w-2xl opacity-85 text-lg leading-relaxed">
              Excellence is expected. Exceptionality is chosen. Between what a
              brand says and what a client feels lies a distance no strategy
              deck can close. We were built to close it — with soul, with system,
              with the invisible details that separate the good from the rare.
            </p>
            <Link to="/manifesto" className="btn-ghost mt-14 inline-flex" data-testid="home-manifesto-link">
              Read the Manifesto <span className="arrow">→</span>
            </Link>
          </Reveal>
        </div>
      </section>
      </div>
      {/* ═══ END CHAPTER I ═══ */}

      {/* ═══ CHAPTER II — Our Values (Warm Cream) ═══ */}
      <div className="relative" style={{ background: "#EDE6D6" }}>
        <Grain opacity={0.035} />

        {/* ─────────── OUR VALUES — Four editorial plates ─────────── */}
        <section
          className="relative max-w-[1440px] mx-auto px-6 md:px-12 lg:px-16 pt-32 md:pt-40 pb-8"
          data-testid="home-values-header"
        >
        <div className="grid grid-cols-12 gap-8">
          <div className="col-span-12 md:col-span-4">
            <Reveal>
              <div className="hairline mb-6" />
              <p className="font-eyebrow" style={{ color: "#8C6B3F" }}>
                Our Values
              </p>
            </Reveal>
          </div>
          <div className="col-span-12 md:col-span-8">
            <Reveal delay={200}>
              <h2
                className="title-caps leading-[0.95]"
                style={{ fontSize: "clamp(2rem, 4.5vw, 4rem)" }}
              >
                THE FOUR{" "}
                <span
                  className="font-script"
                  style={{
                    textTransform: "none",
                    letterSpacing: 0,
                    fontSize: "1.25em",
                    color: "#8C6B3F",
                  }}
                >
                  Anchors
                </span>
                <br />
                OF THE HOUSE
              </h2>
            </Reveal>
          </div>
        </div>
      </section>

      <section
        className="max-w-[1440px] mx-auto px-6 md:px-12 lg:px-16 py-12"
        data-testid="home-triptych"
      >
        <div className="hidden md:grid md:grid-cols-2 lg:grid-cols-4 gap-6 md:gap-8 items-start">
          {[
            {
              src: "https://customer-assets-jt897jd0.emergentagent.net/job_luxury-institute/artifacts/ohcnmcxg_Screenshot%202026-07-20%20at%203.25.00%E2%80%AFpm.png",
              label: "I.",
              caption: "Heritage & Culture",
              subtitle:
                "Because roots give meaning — and we aim to preserve and respect them.",
              aspect: "aspect-[3/4]",
              offset: "md:mt-0",
            },
            {
              src: "https://images.squarespace-cdn.com/content/v1/68f648f746888b590facb861/2a21f893-70f2-451c-ae43-300ac0cfecf7/Screenshot+2026-05-20+at+12.18.52%E2%80%AFpm.png?format=1500w",
              label: "II.",
              caption: "Savoir-Faire & Mastery",
              subtitle:
                "Because mastery is timeless — and it is what luxury is about.",
              aspect: "aspect-[3/4]",
              offset: "md:mt-0",
            },
            {
              src: "https://customer-assets-jt897jd0.emergentagent.net/job_luxury-institute/artifacts/njr5mavb_Screenshot%202026-07-20%20at%203.27.10%E2%80%AFpm.png",
              label: "III.",
              caption: "Humanistic Luxury",
              subtitle:
                "Because luxury is profoundly human — and we always put people at the centre.",
              aspect: "aspect-[3/4]",
              offset: "md:mt-0",
            },
            {
              src: "https://images.squarespace-cdn.com/content/v1/68f648f746888b590facb861/21ef880c-7656-41fe-825f-74a5a531e407/Screenshot+2026-03-26+at+12.20.09%E2%80%AFpm.png?format=1500w",
              label: "IV.",
              caption: "Vision & Innovation",
              subtitle:
                "Because we innovate to last — not just to lead — and we build legacies.",
              aspect: "aspect-[3/4]",
              offset: "md:mt-0",
            },
          ].map((item, idx) => (
            <Reveal key={item.caption} delay={idx * 120} className={item.offset}>
              <div className="p-2 md:p-3" style={{ border: "1px solid #B8B2A6" }}>
                <ParallaxCard
                  src={item.src}
                  alt={item.caption}
                  aspect={item.aspect}
                  strength={0.14 + idx * 0.02}
                  hoverColor
                  data-testid={`home-triptych-image-${idx}`}
                />
              </div>
              <div className="mt-6">
                <p className="font-eyebrow" style={{ color: "#8C6B3F" }}>
                  {item.label}
                </p>
              </div>
              <h3
                className="title-caps mt-4 leading-tight"
                style={{
                  fontSize: "clamp(1.15rem, 1.5vw, 1.5rem)",
                  color: "#14140F",
                  letterSpacing: "0.08em",
                }}
              >
                {item.caption}
              </h3>
              <p
                className="font-body mt-3 opacity-75 leading-relaxed"
                style={{ fontSize: "0.95rem" }}
              >
                {item.subtitle}
              </p>
            </Reveal>
          ))}
        </div>

        {/* Mobile — horizontal scroll swipe hint (double chevron) */}
        <div
          className="md:hidden flex items-center justify-end px-1 mb-3"
          data-testid="home-triptych-swipe-hint"
          aria-hidden="true"
          style={{ color: "#8C6B3F" }}
        >
          <span
            className="swipe-arrow"
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: "4px",
              fontSize: "2.25rem",
              lineHeight: 1,
              letterSpacing: "-0.08em",
              padding: "8px 6px",
            }}
          >
            <span style={{ opacity: 0.55 }}>‹</span>
            <span>‹</span>
          </span>
        </div>

        {/* Mobile — horizontal scroll snap carousel (right-to-left swipe) */}
        <div
          className="md:hidden overflow-x-auto snap-x snap-mandatory -mx-6 px-6 pb-4"
          style={{ scrollbarWidth: "none" }}
          data-testid="home-triptych-mobile"
        >
          <div className="flex gap-6">
            {[
              {
                src: "https://customer-assets-jt897jd0.emergentagent.net/job_luxury-institute/artifacts/ohcnmcxg_Screenshot%202026-07-20%20at%203.25.00%E2%80%AFpm.png",
                label: "I.",
                caption: "Heritage & Culture",
                subtitle:
                  "Because roots give meaning — and we aim to preserve and respect them.",
              },
              {
                src: "https://images.squarespace-cdn.com/content/v1/68f648f746888b590facb861/2a21f893-70f2-451c-ae43-300ac0cfecf7/Screenshot+2026-05-20+at+12.18.52%E2%80%AFpm.png?format=1500w",
                label: "II.",
                caption: "Savoir-Faire & Mastery",
                subtitle:
                  "Because mastery is timeless — and it is what luxury is about.",
              },
              {
                src: "https://customer-assets-jt897jd0.emergentagent.net/job_luxury-institute/artifacts/njr5mavb_Screenshot%202026-07-20%20at%203.27.10%E2%80%AFpm.png",
                label: "III.",
                caption: "Humanistic Luxury",
                subtitle:
                  "Because luxury is profoundly human — and we always put people at the centre.",
              },
              {
                src: "https://images.squarespace-cdn.com/content/v1/68f648f746888b590facb861/21ef880c-7656-41fe-825f-74a5a531e407/Screenshot+2026-03-26+at+12.20.09%E2%80%AFpm.png?format=1500w",
                label: "IV.",
                caption: "Vision & Innovation",
                subtitle:
                  "Because we innovate to last — not just to lead — and we build legacies.",
              },
            ].map((item, idx) => (
              <div
                key={item.caption}
                className="snap-center shrink-0"
                style={{ width: "calc(100vw - 3rem)" }}
              >
                <div className="aspect-[3/4] overflow-hidden">
                  <img
                    src={item.src}
                    alt={item.caption}
                    className="w-full h-full object-cover"
                  />
                </div>
                <p className="font-eyebrow mt-6" style={{ color: "#8C6B3F" }}>
                  {item.label}
                </p>
                <h3
                  className="title-caps mt-3 leading-tight"
                  style={{
                    fontSize: "clamp(1.15rem, 5vw, 1.5rem)",
                    color: "#14140F",
                    letterSpacing: "0.08em",
                  }}
                >
                  {item.caption}
                </h3>
                <p
                  className="font-body mt-3 opacity-75 leading-relaxed"
                  style={{ fontSize: "0.95rem" }}
                >
                  {item.subtitle}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>
      </div>
      {/* ═══ END CHAPTER II ═══ */}

      {/* ═══ CHAPTER III — Strategic Capabilities (Ivory) ═══ */}
      <div className="relative" style={{ background: "#F4EFE7" }}>
        <Grain />

        {/* ─────────── STRATEGIC CAPABILITIES ─────────── */}
        <section
          className="relative max-w-[1440px] mx-auto px-6 md:px-12 lg:px-16 py-32 md:py-40 grid grid-cols-12 gap-8"
          data-testid="home-capabilities"
        >
        <div className="col-span-12 md:col-span-4">
          <Reveal>
            <div className="hairline mb-6" />
            <p className="font-eyebrow" style={{ color: "#8C6B3F" }}>
              Strategic Capabilities
            </p>
          </Reveal>
        </div>
        <div className="col-span-12 md:col-span-8">
          <Reveal delay={200}>
            <h2
              className="title-caps leading-[0.95]"
              style={{ fontSize: "clamp(2.25rem, 5vw, 4.75rem)" }}
            >
              BUILT FOR LUXURY BRANDS READY TO GO
              <br />
              BEYOND{" "}
              <span className="font-script" style={{ textTransform: "none", letterSpacing: 0, fontSize: "1.55em", color: "#8C6B3F" }}>
                Excellence.
              </span>
            </h2>
            <p className="font-body mt-12 opacity-85 text-lg leading-relaxed max-w-2xl">
              Our strategic capabilities are built for luxury brands ready to
              go beyond excellence. We define direction, transmit savoir-faire
              and embed execution into the daily reality of the business.
              Because the top is not reached through strategy alone. It is
              earned through soul, systems, commitment, culture and discipline.
            </p>
            <p className="font-body mt-6 opacity-85 text-lg leading-relaxed max-w-2xl">
              Unlike traditional firms, we do not leave strategy on slides. We
              bring it to the ground, where it becomes behavior, service,
              rituals, and standards that are not only implemented, but
              understood, embodied and scaled.
            </p>
            <p className="font-body mt-6 opacity-85 text-lg leading-relaxed max-w-2xl">
              Through our training and our network of Expert Minds, we build
              the soul and system behind exceptional brands — developing the
              culture, capabilities, and human intelligence required to become
              the next one.
            </p>
          </Reveal>
        </div>
      </section>
      </div>
      {/* ═══ END CHAPTER III ═══ */}

      {/* ═══ CHAPTER IV — Consulting × Training (Deep Ink) ═══ */}
      <div className="relative" style={{ background: "#14140F", color: "#F4EFE7" }}>

        {/* Editorial chapter header removed per user request */}

        {/* ─────────── CONSULTING × TRAINING — Diptych ─────────── */}
        <section
          className="relative max-w-[1440px] mx-auto px-6 md:px-12 lg:px-16 pt-20 pb-40"
          data-testid="home-consulting-training"
        >
          {/* Central editorial spine — vertical hairline (desktop only) */}
          <div
            aria-hidden
            className="hidden md:block absolute pointer-events-none"
            style={{
              left: "calc(50% - 0.5px)",
              top: "0",
              bottom: "clamp(4rem, 8vw, 10rem)",
              width: "1px",
              background: "rgba(244,239,231,0.14)",
            }}
          />

          <div className="grid grid-cols-1 md:grid-cols-2 gap-14 md:gap-24 items-start">
          {[
            {
              n: "I",
              eyebrow: "Advisory",
              title: "Consulting",
              img: "https://customer-assets-jt897jd0.emergentagent.net/job_luxury-institute/artifacts/lk14wck3_Screenshot%202026-07-16%20at%2010.02.53%E2%80%AFpm.png",
              body:
                "We define actionable strategies — and execute them with you, ensuring they become a reality.",
              to: "/services/consulting",
              testid: "home-diptych-consulting",
            },
            {
              n: "II",
              eyebrow: "Transmission",
              title: "Training",
              img: "https://customer-assets-jt897jd0.emergentagent.net/job_luxury-institute/artifacts/z5aw6lut_Screenshot%202026-07-16%20at%2012.32.37%E2%80%AFam.png",
              body:
                "We transmit savoir-faire, cultivate talent and empower leaders to build a lasting legacy.",
              to: "/services/training",
              testid: "home-diptych-training",
            },
          ].map((c, idx) => (
            <Reveal key={c.title} delay={idx * 200}>
              <div className="max-w-[520px] mx-auto">
                {/* Running header — chapter tag */}
                <div className="flex items-baseline justify-between mb-8">
                  <p
                    className="font-eyebrow"
                    style={{
                      color: "#8C6B3F",
                      letterSpacing: "0.32em",
                      fontSize: "0.66rem",
                    }}
                  >
                    {c.eyebrow}
                  </p>
                  <span
                    className="font-eyebrow"
                    style={{
                      color: "rgba(244,239,231,0.35)",
                      letterSpacing: "0.32em",
                      fontSize: "0.66rem",
                    }}
                  >
                    Chapter N°0{idx + 1}
                  </span>
                </div>
                <div
                  className="hairline mb-10"
                  style={{ background: "rgba(244,239,231,0.20)" }}
                />

                {/* Big script title */}
                <h3
                  className="font-script leading-none mb-8"
                  style={{
                    color: "#F4EFE7",
                    fontSize: "clamp(3rem, 5.5vw, 5rem)",
                    textTransform: "none",
                    letterSpacing: 0,
                  }}
                >
                  {c.title}
                </h3>

                {/* Photo — no framed border, just clean crop */}
                <Link
                  to={c.to}
                  data-testid={c.testid}
                  className="relative block group overflow-hidden mx-auto"
                  style={{ maxWidth: "78%" }}
                >
                  <ParallaxCard
                    src={c.img}
                    alt={c.title}
                    aspect="aspect-[4/5]"
                    strength={0.1}
                    filter="grayscale(1) contrast(1.4) brightness(0.86) saturate(0)"
                  />
                  {/* Cinematic vignette */}
                  <div
                    aria-hidden
                    className="absolute inset-0 pointer-events-none"
                    style={{
                      background:
                        "radial-gradient(120% 100% at 50% 45%, rgba(0,0,0,0) 45%, rgba(10,10,8,0.55) 100%)",
                    }}
                  />
                  {/* Warm tone-map */}
                  <div
                    aria-hidden
                    className="absolute inset-0 pointer-events-none"
                    style={{
                      background:
                        "linear-gradient(180deg, rgba(140,107,63,0.08) 0%, rgba(20,20,15,0.28) 100%)",
                      mixBlendMode: "multiply",
                    }}
                  />
                  {/* Bottom gold hairline that fills on hover */}
                  <div
                    aria-hidden
                    className="absolute left-0 bottom-0 h-[2px] transition-all duration-[900ms] ease-out"
                    style={{
                      width: "0%",
                      background: "#8C6B3F",
                    }}
                  />
                  <style>{`
                    .group:hover > [aria-hidden][class*="absolute"][class*="bottom-0"] { width: 100%; }
                  `}</style>
                </Link>

                {/* Body copy */}
                <p className="font-serif-italic mt-10 opacity-85 text-lg leading-relaxed" style={{ color: "#F4EFE7", fontVariationSettings: '"SOFT" 60, "WONK" 1, "opsz" 72' }}>
                  {c.body}
                </p>

                {/* CTA link */}
                <Link
                  to={c.to}
                  className="inline-block mt-10 pb-2 font-eyebrow"
                  style={{
                    color: "#F4EFE7",
                    letterSpacing: "0.32em",
                    fontSize: "0.72rem",
                    borderBottom: "1px solid #8C6B3F",
                    paddingBottom: "0.5rem",
                    minWidth: "14rem",
                  }}
                >
                  Enter {c.title} &nbsp;&nbsp;→
                </Link>
              </div>
            </Reveal>
          ))}
        </div>
        </section>
      </div>
      {/* ═══ END CHAPTER IV ═══ */}

      {/* ═══ CHAPTER V — Our Expertise (Ivory) ═══ */}
      <div className="relative" style={{ background: "#F4EFE7" }}>
        <Grain />

        {/* ─────────── OUR EXPERTISE — 4 pillars ─────────── */}
        <section
          className="relative max-w-[1440px] mx-auto px-6 md:px-12 lg:px-16 py-32"
          data-testid="home-expertise"
        >
        <div className="flex justify-between items-baseline mb-16">
          <div>
            <div className="hairline mb-6" style={{ width: "80px" }} />
            <p className="font-eyebrow" style={{ color: "#8C6B3F" }}>
              Our Expertise
            </p>
          </div>
          <p className="font-eyebrow opacity-50">Five Pillars</p>
        </div>

        <div>
          {[
            {
              n: "I",
              caps: "BRAND STRATEGY",
              dek: "We define who a brand is before we decide how it should show up: identity, narrative, architecture, and positioning. What makes you different, and which business you are really in.",
            },
            {
              n: "II",
              caps: "COMMUNICATION & CONTENT STRATEGY",
              dek: "Words carry culture. We shape how a brand speaks, internally and externally, so language, tone, and message stay consistent from the boardroom to the front line. We build the entire content strategy, digital and physical, taking clients and guests on an intentional journey from the first impression to the last.",
            },
            {
              n: "III",
              caps: "EXPERIENCE DESIGN",
              dek: "We architect the journey a client feels, touchpoint by touchpoint: the emotional logic, the rituals, the sensory detail. Journey mapping, experience strategy, and concept development, built into signature experiences that are trained, embedded, and repeated.",
            },
            {
              n: "IV",
              caps: "OPERATIONAL MASTERY",
              dek: "Vision means nothing without execution. We translate strategy into SOPs, standards, and daily behaviors — the invisible architecture that makes exceptional consistent and intentional. We train the team to deliver it, every day, without you in the room.",
            },
            {
              n: "V",
              caps: "LEADERSHIP & CULTURE",
              dek: "Culture is led, created daily. We work with leadership through executive coaching to embody the values they ask their teams to deliver, because a team performs the culture its leaders actually practice, not the one on the wall.",
            },
          ].map((p, i) => {
            const isHovered = expertiseHover === i;
            const goldOrInk = isHovered ? "#8C6B3F" : "#14140F";
            return (
            <div
              key={p.n}
              onMouseEnter={() => setExpertiseHover(i)}
              onMouseLeave={() => setExpertiseHover(null)}
              className="cursor-default"
            >
            <Reveal
              delay={i * 90}
              className="grid grid-cols-12 gap-4 py-10"
            >
              <div className="col-span-12 hairline mb-2" />
              <div className="col-span-2 md:col-span-1">
                <p
                  className="title-caps"
                  style={{
                    fontSize: "clamp(1.4rem, 2.2vw, 1.9rem)",
                    color: goldOrInk,
                    opacity: 0.55,
                    letterSpacing: "0.06em",
                    transition: "color 600ms ease-out",
                  }}
                >
                  {p.n}
                </p>
              </div>
              <div className="col-span-10 md:col-span-5">
                <h3
                  className="title-caps leading-[0.95]"
                  style={{
                    fontSize: "clamp(1.6rem, 3.2vw, 2.5rem)",
                    color: goldOrInk,
                    transition: "color 600ms ease-out",
                  }}
                >
                  {p.caps}
                </h3>
              </div>
              <div className="col-span-12 md:col-span-6">
                <p
                  className="font-body opacity-75 leading-relaxed"
                  style={{
                    color: goldOrInk,
                    transition: "color 600ms ease-out",
                  }}
                >
                  {p.dek}
                </p>
              </div>
            </Reveal>
            </div>
            );
          })}
          <div className="hairline mt-2" />
        </div>
      </section>
      </div>
      {/* ═══ END CHAPTER V ═══ */}

      {/* ═══ INDUSTRIES — full-width cinematic (Hotel · Boat · Jewel) ═══ */}
      <section
        className="relative w-full overflow-hidden bg-[#0A0A08]"
        style={{ height: "88vh" }}
        data-testid="home-industries-cinematic"
      >
        {[
          {
            src: "https://customer-assets-jt897jd0.emergentagent.net/job_luxury-institute/artifacts/l0kx48p5_HotelduCapEdenRocbyRomainRegladepsd_copie.webp",
            title: "Luxury Hospitality",
          },
          {
            src: "/aquarama-clean.jpg",
            title: "Yachting & Automotive",
          },
          {
            src: "https://customer-assets-jt897jd0.emergentagent.net/job_luxury-institute/artifacts/w5sue1sv_Ana-de-Armas-louis-vuitton-deep-time-robb-report-italia.jpg",
            title: "Luxury Retail",
          },
        ].map((frame, i) => (
          <div
            key={frame.title}
            className="absolute inset-0"
            style={{
              animation: `consultingFade 12s ease-in-out ${i * 4}s infinite`,
              opacity: 0,
            }}
          >
            <img
              src={frame.src}
              alt={frame.title}
              className="w-full h-full object-cover"
              style={{
                filter: "contrast(1.06) saturate(1.05) brightness(0.94)",
                animation: `consultingKen 12s ease-in-out ${i * 4}s infinite`,
              }}
            />
            <div
              className="absolute inset-0 pointer-events-none"
              style={{
                background:
                  "linear-gradient(180deg, rgba(0,0,0,0.40) 0%, rgba(0,0,0,0.50) 50%, rgba(0,0,0,0.65) 100%)",
              }}
            />
            <div className="absolute inset-0 flex items-center justify-center px-6">
              <h2
                className="title-caps text-center"
                style={{
                  color: "#F4EFE7",
                  fontSize: "clamp(2rem, 5.5vw, 5.5rem)",
                  letterSpacing: "0.04em",
                  lineHeight: 1.05,
                  textWrap: "balance",
                  textShadow: "0 2px 24px rgba(0,0,0,0.5)",
                }}
              >
                {frame.title}
              </h2>
            </div>
          </div>
        ))}
      </section>

      {/* ═══ CHAPTER VI — Invitation (Stone) ═══ */}
      <div className="relative" style={{ background: "#DDD6C6" }}>
        <Grain opacity={0.04} />

        {/* Chapter break — hairline */}
        <div className="relative flex justify-center pt-24 pb-2">
          <div className="hairline" style={{ width: "140px", background: "#8C6B3F", opacity: 0.6 }} />
        </div>

        {/* ─────────── INVITATION ─────────── */}
        <section className="relative max-w-[1440px] mx-auto px-6 md:px-12 lg:px-16 py-24 md:py-32">
          <div className="grid grid-cols-12 gap-6">
            <div className="col-span-12 md:col-span-8 md:col-start-3 text-center">
              <Reveal>
                <p className="font-eyebrow" style={{ color: "#8C6B3F" }}>
                  An Invitation
                </p>
                <h2
                  className="title-caps mt-8 leading-[0.95]"
                  style={{ fontSize: "clamp(2.25rem, 5vw, 5rem)" }}
                >
                  FOR THOSE READY TO
                  <br />
                  <span className="font-script" style={{ textTransform: "none", letterSpacing: 0, fontSize: "1.45em", color: "#8C6B3F" }}>
                    become the next.
                  </span>
                </h2>
                <p className="font-body opacity-80 mt-10 max-w-xl mx-auto text-lg leading-relaxed">
                  We work with a limited number of brands, houses, destinations
                  and institutions each year. Introductions are made by request.
                  Every letter is read personally.
                </p>
                <p className="font-body opacity-80 mt-6 max-w-xl mx-auto text-lg leading-relaxed">
                  Ready to transform ambition into lived experience, cultural
                  impact, and operational mastery.
                </p>
                <Link
                  to="/private-inquiry"
                  className="btn-ghost mt-12 inline-flex"
                  data-testid="home-inquiry-cta"
                >
                  Begin Your Journey <span className="arrow">⟶</span>
                </Link>
              </Reveal>
            </div>
          </div>
        </section>
      </div>
      {/* ═══ END CHAPTER VI ═══ */}
    </div>
  );
}
